import type { OpaqueCursorPagination } from '@pillar-portal/api-types';

/** Largest export, so one click can never page through an unbounded table. */
export const LEGAL_CSV_EXPORT_MAX_ROWS = 10_000;
const LEGAL_CSV_EXPORT_PAGE_SIZE = 100;

export type CsvCell = string | number | boolean | null;

/**
 * Quotes one cell. A cell a spreadsheet would read as a formula gets a
 * leading apostrophe, so exported email addresses or names cannot run code.
 */
function csvCell(value: CsvCell): string {
    const text = value === null ? '' : String(value);
    const neutralized = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
    return /[",\r\n]/.test(neutralized) ? `"${neutralized.replace(/"/g, '""')}"` : neutralized;
}

export function toCsv(
    header: ReadonlyArray<string>,
    rows: ReadonlyArray<ReadonlyArray<CsvCell>>
): string {
    return [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n') + '\r\n';
}

export interface LegalCsvExportResult<Row> {
    readonly rows: ReadonlyArray<Row>;
    /** True when the list holds more than LEGAL_CSV_EXPORT_MAX_ROWS rows. */
    readonly truncated: boolean;
}

/** Reads every page of a cursor-paged list, one bounded request at a time. */
export async function collectAllPages<Row>(
    fetchPage: (page: { limit: number; cursor?: string }) => Promise<{
        items: ReadonlyArray<Row>;
        pagination: OpaqueCursorPagination;
    }>
): Promise<LegalCsvExportResult<Row>> {
    const rows: Row[] = [];
    let cursor: string | undefined;
    for (;;) {
        const page = await fetchPage({
            limit: LEGAL_CSV_EXPORT_PAGE_SIZE,
            ...(cursor === undefined ? {} : { cursor }),
        });
        rows.push(...page.items);
        if (!page.pagination.has_more) {
            return { rows, truncated: false };
        }
        if (rows.length >= LEGAL_CSV_EXPORT_MAX_ROWS) {
            return { rows: rows.slice(0, LEGAL_CSV_EXPORT_MAX_ROWS), truncated: true };
        }
        cursor = page.pagination.next_cursor;
    }
}

export function downloadCsv(filename: string, csv: string): void {
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}
